/**
 * Unit configuration definitions.
 * Separates developer-only settings for unit stats and spawn points.
 *
 * Note: DEFAULT_UNIT_CONFIG uses hardcoded values that match UNIT_DEFINITIONS
 * to avoid circular dependencies. The actual game logic uses getUnitStats()
 * from @shared/constants/units which reads from UNIT_DEFINITIONS directly.
 */

import { ConfigCategory, ConfigVisibility, ConfigSchema } from '../types';

/**
 * Unit configuration values.
 * This is the runtime configuration that can be modified by developers.
 * These values override the defaults in UNIT_DEFINITIONS.
 */
export interface UnitConfig {
  // Unit stat overrides (optional - if not set, uses UNIT_DEFINITIONS)
  // Builder stats
  builderHealth?: number;
  builderArmor?: number;
  builderSightRange?: number;
  builderAttackRange?: number;
  builderBaseDamage?: number;
  builderAttackSpeed?: number;
  builderMovementSpeed?: number;

  // Melee stats
  meleeHealth?: number;
  meleeArmor?: number;
  meleeSightRange?: number;
  meleeAttackRange?: number;
  meleeBaseDamage?: number;
  meleeAttackSpeed?: number;
  meleeMovementSpeed?: number;

  // Ranged stats
  rangedHealth?: number;
  rangedArmor?: number;
  rangedSightRange?: number;
  rangedAttackRange?: number;
  rangedBaseDamage?: number;
  rangedAttackSpeed?: number;
  rangedMovementSpeed?: number;

  // Enemy Melee stats
  enemyMeleeHealth?: number;
  enemyMeleeArmor?: number;
  enemyMeleeSightRange?: number;
  enemyMeleeAttackRange?: number;
  enemyMeleeBaseDamage?: number;
  enemyMeleeAttackSpeed?: number;
  enemyMeleeMovementSpeed?: number;

  // Spawn points (for development mode)
  playerSpawnX: number;
  playerSpawnY: number;
  enemySpawnX: number;
  enemySpawnY: number;
}

/**
 * Default unit configuration values.
 * These values match UNIT_DEFINITIONS but are hardcoded here to avoid circular dependencies.
 *
 * The actual game logic uses getUnitStats() from @shared/constants/units which:
 * 1. Checks dev settings first (if available)
 * 2. Falls back to UNIT_DEFINITIONS
 *
 * This DEFAULT_UNIT_CONFIG is only used as a template for the dev settings UI.
 */
export const DEFAULT_UNIT_CONFIG: UnitConfig = {
  // Builder stats (matches UNIT_DEFINITIONS - hardcoded to avoid circular deps)
  builderHealth: 50,
  builderArmor: 0,
  builderSightRange: 7,
  builderAttackRange: 1,
  builderBaseDamage: 5,
  builderAttackSpeed: 1.25,
  builderMovementSpeed: 2,

  // Melee stats (matches UNIT_DEFINITIONS)
  meleeHealth: 100,
  meleeArmor: 5,
  meleeSightRange: 5,
  meleeAttackRange: 1,
  meleeBaseDamage: 25,
  meleeAttackSpeed: 1,
  meleeMovementSpeed: 1,

  // Ranged stats (matches UNIT_DEFINITIONS)
  rangedHealth: 75,
  rangedArmor: 3,
  rangedSightRange: 10,
  rangedAttackRange: 5,
  rangedBaseDamage: 15,
  rangedAttackSpeed: 0.75,
  rangedMovementSpeed: 1.5,

  // Enemy Melee stats (matches UNIT_DEFINITIONS)
  enemyMeleeHealth: 200,
  enemyMeleeArmor: 1,
  enemyMeleeSightRange: 5,
  enemyMeleeAttackRange: 1,
  enemyMeleeBaseDamage: 10,
  enemyMeleeAttackSpeed: 1,
  enemyMeleeMovementSpeed: 1.25,

  // Spawn points (development mode defaults)
  playerSpawnX: 115,
  playerSpawnY: 100,
  enemySpawnX: 123,
  enemySpawnY: 84,
};

/**
 * Get the default unit configuration.
 * Provided for consistency with other config modules.
 */
export function getDefaultUnitConfig(): UnitConfig {
  return { ...DEFAULT_UNIT_CONFIG };
}

/**
 * Unit configuration schema with metadata.
 * Defines which settings are visible/editable by developers.
 */
export const UNIT_CONFIG_SCHEMA: ConfigSchema<UnitConfig> = {
  // Builder Stats
  builderHealth: {
    label: 'Builder Health',
    description: 'Maximum health points for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  builderArmor: {
    label: 'Builder Armor',
    description: 'Armor value for Builder units (reduces incoming damage)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  builderSightRange: {
    label: 'Builder Sight Range',
    description: 'Sight range in tiles (radius) for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 50,
    step: 1,
  },
  builderAttackRange: {
    label: 'Builder Attack Range',
    description: 'Attack range in tiles for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 20,
    step: 1,
  },
  builderBaseDamage: {
    label: 'Builder Base Damage',
    description: 'Base damage per attack for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  builderAttackSpeed: {
    label: 'Builder Attack Speed',
    description: 'Time between attacks in seconds for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  builderMovementSpeed: {
    label: 'Builder Movement Speed',
    description: 'Movement speed in tiles per second for Builder units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },

  // Melee Stats
  meleeHealth: {
    label: 'Melee Health',
    description: 'Maximum health points for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  meleeArmor: {
    label: 'Melee Armor',
    description: 'Armor value for Melee units (reduces incoming damage)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  meleeSightRange: {
    label: 'Melee Sight Range',
    description: 'Sight range in tiles (radius) for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 50,
    step: 1,
  },
  meleeAttackRange: {
    label: 'Melee Attack Range',
    description: 'Attack range in tiles for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 20,
    step: 1,
  },
  meleeBaseDamage: {
    label: 'Melee Base Damage',
    description: 'Base damage per attack for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 200,
    step: 1,
  },
  meleeAttackSpeed: {
    label: 'Melee Attack Speed',
    description: 'Time between attacks in seconds for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  meleeMovementSpeed: {
    label: 'Melee Movement Speed',
    description: 'Movement speed in tiles per second for Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },

  // Ranged Stats
  rangedHealth: {
    label: 'Ranged Health',
    description: 'Maximum health points for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  rangedArmor: {
    label: 'Ranged Armor',
    description: 'Armor value for Ranged units (reduces incoming damage)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  rangedSightRange: {
    label: 'Ranged Sight Range',
    description: 'Sight range in tiles (radius) for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 50,
    step: 1,
  },
  rangedAttackRange: {
    label: 'Ranged Attack Range',
    description: 'Attack range in tiles for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 20,
    step: 1,
  },
  rangedBaseDamage: {
    label: 'Ranged Base Damage',
    description: 'Base damage per attack for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 200,
    step: 1,
  },
  rangedAttackSpeed: {
    label: 'Ranged Attack Speed',
    description: 'Time between attacks in seconds for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  rangedMovementSpeed: {
    label: 'Ranged Movement Speed',
    description: 'Movement speed in tiles per second for Ranged units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },

  // Enemy Melee Stats
  enemyMeleeHealth: {
    label: 'Enemy Melee Health',
    description: 'Maximum health points for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  enemyMeleeArmor: {
    label: 'Enemy Melee Armor',
    description: 'Armor value for Enemy Melee units (reduces incoming damage)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  enemyMeleeSightRange: {
    label: 'Enemy Melee Sight Range',
    description: 'Sight range in tiles (radius) for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 50,
    step: 1,
  },
  enemyMeleeAttackRange: {
    label: 'Enemy Melee Attack Range',
    description: 'Attack range in tiles for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 20,
    step: 1,
  },
  enemyMeleeBaseDamage: {
    label: 'Enemy Melee Base Damage',
    description: 'Base damage per attack for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 200,
    step: 1,
  },
  enemyMeleeAttackSpeed: {
    label: 'Enemy Melee Attack Speed',
    description: 'Time between attacks in seconds for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },
  enemyMeleeMovementSpeed: {
    label: 'Enemy Melee Movement Speed',
    description: 'Movement speed in tiles per second for Enemy Melee units',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },

  // Spawn Points
  playerSpawnX: {
    label: 'Player Spawn X',
    description: 'X coordinate for player unit spawn point (development mode)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 500,
    step: 1,
  },
  playerSpawnY: {
    label: 'Player Spawn Y',
    description: 'Y coordinate for player unit spawn point (development mode)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 500,
    step: 1,
  },
  enemySpawnX: {
    label: 'Enemy Spawn X',
    description: 'X coordinate for enemy unit spawn point (development mode)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 500,
    step: 1,
  },
  enemySpawnY: {
    label: 'Enemy Spawn Y',
    description: 'Y coordinate for enemy unit spawn point (development mode)',
    category: ConfigCategory.UNITS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 500,
    step: 1,
  },
};
